/*
 * cart-rewards: <cart-gwp> progress bar + gift-with-purchase logic. Markup and config come from
 * snippets/cart-gwp.liquid; shared helpers from assets/cart-rewards.js.
 *
 * Prestige re-renders the whole drawer after every cart change, so each change creates a fresh <cart-gwp>.
 * On connect it renders its state, then asks the controller to reconcile gift lines with that state:
 *   - remove gift lines whose tier is locked / gone / wrong variant (and show the tier's removed message)
 *   - reset gift quantities to 1
 *   - auto-add gifts for unlocked "auto" tiers, unless the customer removed that gift this session
 * Our own cart writes re-render the drawer too, which reconnects <cart-gwp>; by then the cart matches the
 * state, so nothing more happens. A lock, a debounce and a circuit breaker stop any possible loop.
 */
(() => {
  const CR = window.CartRewards;
  if (!CR || window.customElements.get('cart-gwp')) return;

  const OPT_OUT_KEY = 'cart-rewards:gift-opt-out';
  const UNLOCKED_KEY = 'cart-rewards:unlocked';
  const HISTORY_KEY = 'cart-rewards:auto-history';
  const FLASH_MS = 8000;
  const MAX_AUTO_WRITES = 6; // automatic cart writes allowed per minute before we stop and log

  const storage = {
    getSet(key) {
      try { return new Set(JSON.parse(sessionStorage.getItem(key) || '[]')); } catch (error) { return new Set(); }
    },
    setSet(key, set) {
      try { sessionStorage.setItem(key, JSON.stringify([...set])); } catch (error) { /* private mode etc. */ }
    }
  };

  const fillTemplate = (template, vars) => (template || '').replace(/\{(remaining|threshold|gift|total)\}/g, (match, key) => vars[key] ?? '');

  const templateVars = (tier) => ({
    remaining: CR.formatMoney(tier.remaining),
    threshold: CR.formatMoney(tier.thresholdCents),
    gift: tier.gift,
    total: CR.formatMoney(tier.total)
  });

  function computeState(config) {
    const now = Date.now() / 1000;
    const rate = CR.currencyRate();
    const tiers = config.tiers
      .filter((tier) => (!tier.startsAt || tier.startsAt <= now) && (!tier.endsAt || tier.endsAt >= now))
      .map((tier) => {
        const thresholdCents = Math.round(tier.threshold * rate);
        return { ...tier, thresholdCents, unlocked: tier.total >= thresholdCents, remaining: Math.max(0, thresholdCents - tier.total) };
      });

    let eligibleGifts = tiers.filter((tier) => tier.type === 'gift' && tier.unlocked);
    if (config.giftMode === 'highest' && eligibleGifts.length > 1) {
      eligibleGifts = eligibleGifts.slice(-1);
    }

    return { tiers, eligibleGifts, byHandle: new Map(tiers.map((tier) => [tier.handle, tier])) };
  }

  /* ---------------------------------------------------------------- controller (one per page) */

  const controller = {
    element: null,
    busy: false,
    rerun: false,
    timer: null,
    flash: null, // { tier, kind: 'added' | 'removed' | 'error', at }
    lastProgress: null,
    failed: new Set(), // tiers whose gift could not be added during this page view

    schedule(element) {
      this.element = element;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.reconcile(), 250);
    },

    flashFor(handle) {
      if (this.flash && this.flash.tier === handle && Date.now() - this.flash.at < FLASH_MS) return this.flash;
      return null;
    },

    allowAutoWrite() {
      const now = Date.now();
      let history = [];
      try { history = JSON.parse(sessionStorage.getItem(HISTORY_KEY) || '[]').filter((time) => now - time < 60000); } catch (error) { /* ignore */ }
      if (history.length >= MAX_AUTO_WRITES) {
        console.warn('[cart-rewards] Paused automatic gift updates: too many cart changes in one minute.');
        return false;
      }
      history.push(now);
      try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch (error) { /* ignore */ }
      return true;
    },

    async run(task) {
      if (this.busy) {
        this.rerun = true;
        return;
      }
      this.busy = true;
      this.element?.setBusy(true);
      try {
        await task();
        await CR.publishCart();
      } catch (error) {
        console.warn('[cart-rewards]', error);
        try { await CR.publishCart(); } catch (publishError) { /* drawer keeps its current content */ }
      } finally {
        this.busy = false;
        this.element?.setBusy(false);
        if (this.rerun) {
          this.rerun = false;
          this.schedule(this.element);
        }
      }
    },

    // Bring gift lines in line with the current state.
    reconcile() {
      const element = this.element;
      if (!element || !element.isConnected || !element.state || window.Shopify?.designMode) return;
      if (this.busy) {
        this.rerun = true;
        return;
      }

      const { config, state } = element;
      const eligible = new Set(state.eligibleGifts.map((tier) => tier.handle));
      const optOut = storage.getSet(OPT_OUT_KEY);
      const updates = {};
      const removed = [];
      const kept = new Set();

      config.giftLines.forEach((line) => {
        const tier = state.byHandle.get(line.tier);
        const keep = tier && eligible.has(line.tier) && !kept.has(line.tier) && line.variantId === tier.variantId;
        if (!keep) {
          updates[line.key] = 0;
          removed.push(line.tier);
        } else {
          kept.add(line.tier);
          if (line.quantity !== 1) updates[line.key] = 1;
        }
      });

      const toAdd = state.eligibleGifts.filter((tier) =>
        tier.addMode === 'auto' && !kept.has(tier.handle) && !optOut.has(tier.handle) && !this.failed.has(tier.handle));

      if (!Object.keys(updates).length && !toAdd.length) return;
      if (!this.allowAutoWrite()) return;

      this.run(async () => {
        if (Object.keys(updates).length) {
          await CR.cartRequest('update', { updates });
          removed.forEach((handle) => {
            const tier = state.byHandle.get(handle);
            CR.track('gwp_removed', { tier: handle, reason: tier && !tier.unlocked ? 'below_threshold' : 'not_eligible' });
            if (tier && !tier.unlocked && tier.removedMessage) {
              this.flash = { tier: handle, kind: 'removed', at: Date.now() };
              CR.announce(fillTemplate(tier.removedMessage, templateVars(tier)));
            }
          });
        }
        if (toAdd.length) await this.addGifts(toAdd, 'auto');
      });
    },

    async addGifts(tiers, source) {
      try {
        await CR.cartRequest('add', {
          items: tiers.map((tier) => ({ id: tier.variantId, quantity: 1, properties: { _gwp_tier: tier.handle } }))
        });
      } catch (error) {
        tiers.forEach((tier) => {
          this.failed.add(tier.handle);
          this.flash = { tier: tier.handle, kind: 'error', at: Date.now() };
        });
        CR.announce('Sorry, this gift is no longer available.');
        throw error;
      }
      tiers.forEach((tier) => {
        this.flash = { tier: tier.handle, kind: 'added', at: Date.now() };
        CR.track('gwp_added', { tier: tier.handle, variant_id: tier.variantId, product_id: tier.productId, source });
        CR.announce(fillTemplate(tier.addedMessage || tier.unlockedMessage, templateVars(tier)));
      });
    },

    addGiftFromButton(handle) {
      const tier = this.element?.state?.byHandle.get(handle);
      if (!tier) return;
      const optOut = storage.getSet(OPT_OUT_KEY);
      optOut.delete(handle);
      storage.setSet(OPT_OUT_KEY, optOut);
      this.failed.delete(handle);
      this.run(() => this.addGifts([tier], 'button'));
    }
  };

  // A customer removing a gift line (drawer or cart page) opts that gift out of auto-add for the session.
  document.addEventListener('line-item:will-change', (event) => {
    const handle = event.target?.getAttribute?.('data-gwp-tier');
    if (!handle || event.detail?.targetQuantity !== 0) return;
    const optOut = storage.getSet(OPT_OUT_KEY);
    optOut.add(handle);
    storage.setSet(OPT_OUT_KEY, optOut);
    CR.track('gwp_removed', { tier: handle, reason: 'customer' });
  });

  /* ---------------------------------------------------------------- element */

  class CartGwp extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-role="config"]').textContent);
        CR.analyticsEnabled = this.config.analytics === true;
        this.state = computeState(this.config);
        this.render();
        this.addEventListener('click', this.onClick);
        controller.schedule(this);
      } catch (error) {
        console.warn('[cart-rewards] progress bar disabled', error);
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const button = event.target.closest('[data-action="add-gift"]');
      if (!button || button.disabled) return;
      event.preventDefault();
      controller.addGiftFromButton(button.getAttribute('data-tier'));
    };

    setBusy(busy) {
      this.querySelectorAll('[data-action="add-gift"]').forEach((button) => {
        button.disabled = busy;
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    }

    render() {
      const { tiers, eligibleGifts, byHandle } = this.state;
      if (!tiers.length) {
        this.hidden = true;
        return;
      }

      const last = tiers[tiers.length - 1];
      const next = tiers.find((tier) => !tier.unlocked);
      const lastUnlocked = [...tiers].reverse().find((tier) => tier.unlocked);

      // Fill up to the next locked marker, never past it (each tier may count a slightly different total).
      let progress = 1;
      if (next) {
        const index = tiers.indexOf(next);
        const from = index > 0 ? tiers[index - 1].thresholdCents : 0;
        const span = next.thresholdCents - from;
        const part = span > 0 ? Math.min(1, Math.max(0, (next.total - from) / span)) : 0;
        progress = (from + part * span) / last.thresholdCents;
      }
      // The drawer re-render creates a new bar: start it where the old one was so the fill animates (CSS handles
      // prefers-reduced-motion by removing the transition).
      const previous = controller.lastProgress;
      controller.lastProgress = progress;
      if (previous != null && previous !== progress) {
        this.style.setProperty('--cart-gwp-progress', previous.toFixed(4));
        requestAnimationFrame(() => requestAnimationFrame(() => this.style.setProperty('--cart-gwp-progress', progress.toFixed(4))));
      } else {
        this.style.setProperty('--cart-gwp-progress', progress.toFixed(4));
      }
      this.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', String(Math.round(progress * 100)));

      this.querySelectorAll('.cart-gwp__marker').forEach((marker) => {
        const tier = byHandle.get(marker.getAttribute('data-tier'));
        marker.hidden = !tier;
        marker.classList.toggle('is-unlocked', Boolean(tier?.unlocked));
      });

      // Messages
      let message;
      let secondary = '';
      if (next) {
        message = fillTemplate(next.lockedMessage, templateVars(next));
        if (lastUnlocked) secondary = fillTemplate(lastUnlocked.unlockedMessage, templateVars(lastUnlocked));
      } else {
        message = fillTemplate(this.config.completedMessage || last.unlockedMessage, templateVars(last));
      }
      const removedFlash = tiers.find((tier) => controller.flashFor(tier.handle)?.kind === 'removed' && !tier.unlocked);
      if (removedFlash?.removedMessage) {
        secondary = message;
        message = fillTemplate(removedFlash.removedMessage, templateVars(removedFlash));
      }

      this.setText('[data-role="message"]', message || ' ');
      this.setText('[data-role="secondary"]', secondary);

      // Gift rows: only for tiers the customer can claim right now
      const eligible = new Set(eligibleGifts.map((tier) => tier.handle));
      const inCart = new Set(this.config.giftLines.map((line) => line.tier));
      this.querySelectorAll('.cart-gwp__gift').forEach((row) => {
        const handle = row.getAttribute('data-tier');
        const tier = byHandle.get(handle);
        const flash = controller.flashFor(handle);
        row.hidden = !tier || !eligible.has(handle);
        if (row.hidden) return;

        // Before it's added the main message already says it's unlocked, so the row just names the gift
        const added = inCart.has(handle);
        let rowMessage = added ? fillTemplate(tier.addedMessage || tier.unlockedMessage, templateVars(tier)) : tier.gift;
        if (!added && flash?.kind === 'error') rowMessage = 'Sorry, this gift is no longer available.';
        row.querySelector('[data-role="gift-message"]').textContent = rowMessage;
        row.classList.toggle('is-added', added);
        const button = row.querySelector('[data-action="add-gift"]');
        if (button) {
          button.hidden = added || controller.failed.has(handle);
          button.textContent = fillTemplate(button.textContent, templateVars(tier));
        }
      });

      this.trackUnlocks(tiers);
    }

    setText(selector, text) {
      const element = this.querySelector(selector);
      if (!element) return;
      element.textContent = text;
      if (element.hasAttribute('hidden') || selector.includes('secondary')) element.hidden = !text;
    }

    trackUnlocks(tiers) {
      const previous = storage.getSet(UNLOCKED_KEY);
      const current = new Set(tiers.filter((tier) => tier.unlocked).map((tier) => tier.handle));
      current.forEach((handle) => {
        if (previous.has(handle)) return;
        const tier = tiers.find((item) => item.handle === handle);
        CR.track('gwp_unlocked', { tier: handle, type: tier.type, threshold: tier.thresholdCents / 100, total: tier.total / 100 });
        if (document.querySelector('cart-drawer')?.hasAttribute('open')) {
          CR.announce(fillTemplate(tier.unlockedMessage, templateVars(tier)));
        }
      });
      storage.setSet(UNLOCKED_KEY, current);
    }
  }

  window.customElements.define('cart-gwp', CartGwp);
})();
